// ============================================================================
// 3. RAW RPC CALLS - Create raw JSON-RPC calls
// ============================================================================

import { encodeFunctionData } from 'viem'
import { MULTICALL_DEFAULT_BATCH_SIZE } from './constants'

export interface PreparedCall {
  address: string
  functionName: string
  params: any[]
  abi: any[]
}

export interface RawRpcCall {
  jsonrpc: '2.0'
  id: number
  method: 'eth_call'
  params: [
    {
      to: string
      data: string
    },
    string, // block number
  ]
}

export interface RawRpcBatch {
  batchIndex: number
  calls: RawRpcCall[]
  callMetadata: PreparedCall[]
}

export const multicall3Abi = [
  {
    type: 'function',
    name: 'aggregate3',
    stateMutability: 'payable',
    inputs: [
      {
        name: 'calls',
        type: 'tuple[]',
        components: [
          { name: 'target', type: 'address' },
          { name: 'allowFailure', type: 'bool' },
          { name: 'callData', type: 'bytes' },
        ],
      },
    ],
    outputs: [
      {
        name: 'returnData',
        type: 'tuple[]',
        components: [
          { name: 'success', type: 'bool' },
          { name: 'returnData', type: 'bytes' },
        ],
      },
    ],
  },
] as const

export interface MulticallRpcBatch {
  batchIndex: number
  call: RawRpcCall
  callMetadata: PreparedCall[]
}

export function createRawRpcCalls(
  preparedCalls: PreparedCall[],
  batchSize: number = MULTICALL_DEFAULT_BATCH_SIZE,
  blockTag: string = 'latest',
): RawRpcBatch[] {
  const batches: RawRpcBatch[] = []

  for (let i = 0; i < preparedCalls.length; i += batchSize) {
    const batchCalls = preparedCalls.slice(i, i + batchSize)

    const rpcCalls: RawRpcCall[] = batchCalls.map((call, idx) => {
      const data = encodeFunctionData({
        abi: call.abi,
        functionName: call.functionName,
        args: call.params,
      })

      return {
        jsonrpc: '2.0',
        id: i + idx + 1,
        method: 'eth_call',
        params: [
          {
            to: call.address,
            data,
          },
          blockTag,
        ],
      }
    })

    batches.push({
      batchIndex: Math.floor(i / batchSize),
      calls: rpcCalls,
      callMetadata: batchCalls,
    })
  }

  return batches
}

/**
 * Creates a single multicall3 aggregate3 RPC call that batches all prepared calls
 * This reduces multiple RPC calls to a single call, avoiding rate limiting
 */
export function createMulticallRpcCall(
  preparedCalls: PreparedCall[],
  multicallAddress: string,
  batchSize: number = MULTICALL_DEFAULT_BATCH_SIZE,
  blockTag: string = 'latest',
  allowFailure: boolean = true,
): MulticallRpcBatch[] {
  const batches: MulticallRpcBatch[] = []

  for (let i = 0; i < preparedCalls.length; i += batchSize) {
    const batchCalls = preparedCalls.slice(i, i + batchSize)

    // Prepare the aggregate3 calls array
    const aggregate3Calls = batchCalls.map((call) => {
      const callData = encodeFunctionData({
        abi: call.abi,
        functionName: call.functionName,
        args: call.params,
      })

      return {
        target: call.address as `0x${string}`,
        allowFailure,
        callData,
      }
    })

    // Encode the aggregate3 function call
    const multicallData = encodeFunctionData({
      abi: multicall3Abi,
      functionName: 'aggregate3',
      args: [aggregate3Calls],
    })

    const rpcCall: RawRpcCall = {
      jsonrpc: '2.0',
      id: Math.floor(i / batchSize) + 1,
      method: 'eth_call',
      params: [
        {
          to: multicallAddress,
          data: multicallData,
        },
        blockTag,
      ],
    }

    batches.push({
      batchIndex: Math.floor(i / batchSize),
      call: rpcCall,
      callMetadata: batchCalls,
    })
  }

  return batches
}
